import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Mail, CalendarClock, Gauge } from "lucide-react";
import { fmtDate } from "@/lib/dates";

export default function AdminLeads() {
  const [demoReqs, setDemoReqs] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/demo-requests").then((r) => r.data),
      api.get("/admin/compliance-quizzes").then((r) => r.data),
      api.get("/admin/contacts").then((r) => r.data),
    ]).then(([d, q, c]) => { setDemoReqs(d); setQuizzes(q); setContacts(c); }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>;

  return (
    <div data-testid="admin-leads-page" className="space-y-10">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Admin · sales</div>
        <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Leads & signals</h1>
        <p className="mt-2 text-stone-500">Everything coming in from the landing page.</p>
      </header>

      <Tabs defaultValue="demos">
        <TabsList className="bg-stone-100 p-1 rounded-xl h-auto">
          <TabsTrigger value="demos" data-testid="admin-tab-demos" className="data-[state=active]:bg-white rounded-lg px-4 py-2">
            Demo requests ({demoReqs.length})
          </TabsTrigger>
          <TabsTrigger value="quizzes" data-testid="admin-tab-quizzes" className="data-[state=active]:bg-white rounded-lg px-4 py-2">
            Compliance quizzes ({quizzes.length})
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="data-[state=active]:bg-white rounded-lg px-4 py-2">
            Newsletter ({contacts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="demos" className="mt-6">
          <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
            <table className="w-full" data-testid="admin-demos-table">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-[11px] uppercase tracking-widest text-stone-500">
                  <th className="text-left font-semibold px-5 py-3">Name / Center</th>
                  <th className="text-left font-semibold px-5 py-3">Email</th>
                  <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Children</th>
                  <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">Message</th>
                  <th className="text-right font-semibold px-5 py-3">When</th>
                </tr>
              </thead>
              <tbody>
                {demoReqs.length === 0 ? (
                  <tr><td colSpan="5" className="px-5 py-14 text-center text-stone-500 text-sm">No demo requests yet.</td></tr>
                ) : demoReqs.map((d) => (
                  <tr key={d.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                    <td className="px-5 py-4">
                      <div className="text-sm font-medium text-stone-900">{d.name}</div>
                      <div className="text-[11px] text-stone-500">{d.center_name}</div>
                    </td>
                    <td className="px-5 py-4 text-sm text-stone-700"><Mail className="w-3 h-3 inline text-stone-400 mr-1" />{d.email}</td>
                    <td className="px-5 py-4 text-sm text-stone-700 hidden md:table-cell">{d.num_children || "—"}</td>
                    <td className="px-5 py-4 text-xs text-stone-500 hidden lg:table-cell truncate max-w-xs">{d.message || "—"}</td>
                    <td className="px-5 py-4 text-right text-xs text-stone-500"><CalendarClock className="w-3 h-3 inline mr-1" />{fmtDate(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="quizzes" className="mt-6">
          <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
            <table className="w-full" data-testid="admin-quizzes-table">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-[11px] uppercase tracking-widest text-stone-500">
                  <th className="text-left font-semibold px-5 py-3">Email</th>
                  <th className="text-left font-semibold px-5 py-3 hidden sm:table-cell">Center</th>
                  <th className="text-left font-semibold px-5 py-3">Score</th>
                  <th className="text-left font-semibold px-5 py-3">Risk</th>
                  <th className="text-right font-semibold px-5 py-3">When</th>
                </tr>
              </thead>
              <tbody>
                {quizzes.length === 0 ? (
                  <tr><td colSpan="5" className="px-5 py-14 text-center text-stone-500 text-sm">No quiz submissions yet.</td></tr>
                ) : quizzes.map((q) => (
                  <tr key={q.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                    <td className="px-5 py-4 text-sm text-stone-900">{q.email}</td>
                    <td className="px-5 py-4 text-sm text-stone-700 hidden sm:table-cell">{q.center_name || "—"}</td>
                    <td className="px-5 py-4"><div className="flex items-center gap-2"><Gauge className="w-3.5 h-3.5 text-stone-400" /><span className="font-display font-semibold text-stone-900">{q.score}/100</span></div></td>
                    <td className="px-5 py-4">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${
                        q.risk_level === "low" ? "bg-emerald-100 text-emerald-800" :
                        q.risk_level === "moderate" ? "bg-orange-100 text-orange-800" :
                        "bg-red-100 text-red-800"
                      }`}>{q.risk_level}</span>
                    </td>
                    <td className="px-5 py-4 text-right text-xs text-stone-500">{fmtDate(q.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="contacts" className="mt-6">
          <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
            <table className="w-full" data-testid="admin-contacts-table">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-[11px] uppercase tracking-widest text-stone-500">
                  <th className="text-left font-semibold px-5 py-3">Email</th>
                  <th className="text-right font-semibold px-5 py-3">When</th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 ? (
                  <tr><td colSpan="2" className="px-5 py-14 text-center text-stone-500 text-sm">No newsletter subscribers yet.</td></tr>
                ) : contacts.map((c) => (
                  <tr key={c.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                    <td className="px-5 py-4 text-sm text-stone-900">{c.email}</td>
                    <td className="px-5 py-4 text-right text-xs text-stone-500">{fmtDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
